import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { LOGO } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const ROLES = [
  ["artist", "Solo Artist"],
  ["label", "Label"],
];

export default function Register() {
  const nav = useNavigate();
  const [f, setF] = useState({ name: "", email: "", password: "", role: "artist", profile_name: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const upd = (k, v) => setF({ ...f, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register", f);
      localStorage.setItem("aru_kyc_token", data.kyc_token);
      nav("/complete-kyc");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dark flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md p-8" data-testid="register-page">
        <img src={LOGO} alt="ARU Music Records" className="mx-auto mb-6 h-12 w-auto" />
        {done ? (
          <div className="text-center" data-testid="register-success">
            <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-400" />
            <h2 className="mt-4 font-heading text-xl font-bold">Registration submitted</h2>
            <p className="mt-2 text-sm text-muted-foreground">Your account is pending Super Admin approval. You'll be able to log in once approved.</p>
            <Button className="mt-6 w-full" onClick={() => nav("/login")} data-testid="back-to-login">Back to Login</Button>
          </div>
        ) : (
          <>
            <h2 className="text-center font-heading text-2xl font-bold">Create an account</h2>
            <p className="mt-1 text-center text-sm text-muted-foreground">Accounts require Super Admin approval before first login.</p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div><Label>Full Name</Label><Input value={f.name} onChange={(e) => upd("name", e.target.value)} required data-testid="reg-name" className="mt-1" /></div>
              <div><Label>Email</Label><Input type="email" value={f.email} onChange={(e) => upd("email", e.target.value)} required data-testid="reg-email" className="mt-1" /></div>
              <div><Label>Password</Label><Input type="password" value={f.password} onChange={(e) => upd("password", e.target.value)} required data-testid="reg-password" className="mt-1" /></div>
              <div>
                <Label>Account Type</Label>
                <Select value={f.role} onValueChange={(v) => upd("role", v)}>
                  <SelectTrigger className="mt-1" data-testid="reg-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {(f.role === "artist" || f.role === "label") && (
                <div>
                  <Label>{f.role === "artist" ? "Artist / Stage Name" : "Label Name"}</Label>
                  <Input value={f.profile_name} onChange={(e) => upd("profile_name", e.target.value)} data-testid="reg-profile-name" className="mt-1" />
                </div>
              )}
              <Button type="submit" className="w-full" disabled={busy} data-testid="register-submit">
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Submit Registration
              </Button>
            </form>
            <p className="mt-4 text-center text-sm text-muted-foreground">
              Already have an account? <Link to="/login" className="text-primary" data-testid="to-login-link">Sign in</Link>
            </p>
          </>
        )}
      </Card>
    </div>
  );
}
